
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/base/Button';
import { Card } from '../../components/base/Card';
import { SEO, getOrganizationSchema, getLocalBusinessSchema } from '../../utils/seo';

export function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [counters, setCounters] = useState({ years: 0, projects: 0, onTime: 0 });

  const services = [
    {
      icon: 'ri-settings-4-line',
      title: 'CNC Torna',
      description: 'Milimetrik doğrulukta döner parça üretimi ve hassas işleme',
      link: '/hizmetler/cnc-torna'
    },
    {
      icon: 'ri-tools-line',
      title: 'CNC Dik İşleme Merkezi',
      description: 'Karmaşık geometrili parçalar için çok eksenli işleme',
      link: '/hizmetler/cnc-dik-isleme'
    },
    {
      icon: 'ri-hammer-line',
      title: 'Üniversal Torna & Freze',
      description: 'Küçük çaplı işler ve revizyon çalışmaları',
      link: '/hizmetler/universal-torna-freze'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Kaynak',
      description: 'TIG, MIG ve punta kaynak ile dayanıklı birleştirme',
      link: '/hizmetler/kaynak'
    },
    {
      icon: 'ri-printer-line',
      title: '3D Printer Atölyesi',
      description: 'Hızlı prototipleme ve FDM/SLA baskı hizmetleri',
      link: '/hizmetler/3d-printer'
    }
  ];

  const projects = [
    {
      image: 'https://public.readdy.ai/ai/img_res/3b402f13414072f4ec553d0e56656e9e.jpg',
      title: 'Otomotiv Motor Parçası',
      category: 'CNC Torna',
      duration: '3 gün'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=complex%20aerospace%20component%20being%20machined%20on%20CNC%20milling%20center%2C%20red%20accent%20lighting%2C%20precise%20cutting%20operation%2C%20industrial%20workshop%20setting&width=400&height=300&seq=2&orientation=landscape',
      title: 'Havacılık Komponenti',
      category: 'CNC Dik İşleme',
      duration: '5 gün'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=TIG%20welding%20operation%20on%20stainless%20steel%20parts%2C%20bright%20welding%20arc%2C%20red%20and%20white%20workshop%20environment%2C%20professional%20welder%20at%20work&width=400&height=300&seq=3&orientation=landscape',
      title: 'Paslanmaz Çelik Kaynak',
      category: 'Kaynak',
      duration: '2 gün'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=3D%20printed%20prototype%20parts%20in%20white%20and%20red%20colors%2C%20modern%203D%20printer%20in%20operation%2C%20clean%20workshop%20environment%2C%20detailed%20surface%20finish&width=400&height=300&seq=4&orientation=landscape',
      title: 'Prototip Üretimi',
      category: '3D Baskı',
      duration: '1 gün'
    }
  ];

  const blogPosts = [
    {
      image: 'https://readdy.ai/api/search-image?query=CNC%20lathe%20cutting%20metal%20part%20with%20precision%2C%20red%20industrial%20lighting%2C%20metal%20chips%20flying%2C%20technical%20manufacturing%20process&width=300&height=200&seq=5&orientation=landscape',
      title: 'CNC Tornalama İpuçları ve Teknikler',
      excerpt: 'Hassas tornalama için en önemli faktörler ve dikkat edilmesi gereken noktalar.',
      date: '15 Aralık 2024',
      category: 'CNC'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=3D%20printer%20vs%20CNC%20machine%20comparison%2C%20modern%20workshop%20setting%2C%20red%20and%20white%20theme%2C%20manufacturing%20technology%20showcase&width=300&height=200&seq=6&orientation=landscape',
      title: '3D Baskı mı İşleme mi? Doğru Seçim',
      excerpt: 'Prototip üretiminde 3D baskı ve geleneksel işleme yöntemlerinin karşılaştırması.',
      date: '12 Aralık 2024',
      category: 'Teknoloji'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=welding%20quality%20inspection%20with%20measuring%20tools%2C%20professional%20workshop%20environment%2C%20red%20accent%20lighting%2C%20quality%20control%20process&width=300&height=200&seq=7&orientation=landscape',
      title: 'Kaynak Sonrası Muayene Yöntemleri',
      excerpt: 'Kaynak kalitesini değerlendirmek için kullanılan test ve muayene teknikleri.',
      date: '10 Aralık 2024',
      category: 'Kalite'
    }
  ];

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      getOrganizationSchema(),
      getLocalBusinessSchema(),
      {
        "@type": "WebSite",
        "name": "3DP Makina",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/search?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % projects.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;
      
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        
        setCounters({
          years: Math.floor(10 * progress),
          projects: Math.floor(500 * progress),
          onTime: Math.floor(98 * progress)
        });
        
        if (step >= steps) {
          clearInterval(timer);
          setCounters({ years: 10, projects: 500, onTime: 98 });
        }
      }, stepDuration);
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        animateCounters();
        observer.disconnect();
      }
    });

    const statsSection = document.getElementById('stats-section');
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="CNC Torna, Freze, Kaynak ve 3D Baskı | Precision Manufacturing"
        description="CNC torna, dik işleme, freze, kaynak ve 3D prototipleme hizmetleri. Hızlı teklif, kalite garantisi. Hassas imalatta güvenilir çözüm ortağınız."
        keywords="CNC torna, CNC freze, kaynak, 3D baskı, hassas imalat, metal işleme, prototipleme, Ankara"
        canonical={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/`}
        ogTitle="CNC Torna, Freze, Kaynak ve 3D Baskı | Precision Manufacturing"
        ogDescription="CNC torna, dik işleme, freze, kaynak ve 3D prototipleme hizmetleri. Hızlı teklif, kalite garantisi."
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/`}
        jsonLd={homeSchema}
      />
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.6), rgba(193, 18, 31, 0.3)), url('https://readdy.ai/api/search-image?query=modern%20CNC%20manufacturing%20workshop%20with%20red%20and%20white%20industrial%20lighting%2C%20precision%20machining%20operation%20in%20progress%2C%20clean%20professional%20environment%2C%20metal%20parts%20being%20manufactured&width=1920&height=1080&seq=8&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Hassas İmalatta<br />
            <span className="text-[#C1121F]">Güvenilir Çözüm</span><br />
            Ortağınız
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            CNC torna, dik işleme, freze, kaynak ve 3D baskı hizmetleriyle tek çatı altında üretim
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/teklif-al">
              <Button size="lg" className="text-lg px-8 py-4 whitespace-nowrap">
                <i className="ri-mail-line mr-2"></i>
                Teklif Al
              </Button>
            </Link>
            <Link to="/projeler">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-[#161616] whitespace-nowrap">
                <i className="ri-eye-line mr-2"></i>
                Projeleri Gör
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Hizmetlerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern teknoloji ve uzman kadromuzla her türlü imalat ihtiyacınıza çözüm üretiyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} hover className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <Link to={service.link}>
                  <Button size="sm" className="whitespace-nowrap">
                    Detayları Gör
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Referans Projelerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Gerçekleştirdiğimiz projelerle kalite ve hassasiyet standartlarımızı keşfedin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project, index) => (
              <Card key={index} hover className="overflow-hidden">
                <img 
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <span className="bg-[#C1121F] text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.category}
                  </span>
                  <h3 className="text-xl font-semibold text-[#161616] mt-4 mb-3">
                    {project.title}
                  </h3>
                  <Button size="sm" variant="outline" className="whitespace-nowrap">
                    Detayları Gör
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/projeler">
              <Button size="lg" className="whitespace-nowrap">
                Tüm Projeleri Gör
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section id="stats-section" className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-4">{counters.years}+</div>
              <div className="text-xl">Yıl Deneyim</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-4">{counters.projects}+</div>
              <div className="text-xl">Tamamlanan Proje</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-4">%{counters.onTime}</div>
              <div className="text-xl">Zamanında Teslim</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-4">50+</div>
              <div className="text-xl">Mutlu Müşteri</div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Blog Yazılarımız
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CNC, kaynak, 3D baskı ve kalite kontrol konularında teknik bilgiler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'CNC Tornalama İpuçları',
                excerpt: 'Hassas işleme için dikkat edilmesi gereken önemli noktalar.',
                date: '15 Aralık 2024',
                slug: 'cnc-tornalama-ipuclari-hassas-isleme-teknikleri',
                image: 'https://readdy.ai/api/search-image?query=CNC%20lathe%20machine%20in%20operation%20with%20metal%20chips%2C%20precision%20turning%20process%2C%20Turkish%20manufacturing%20workshop%20with%20red%20and%20white%20industrial%20lighting&width=400&height=250&seq=home-blog1&orientation=landscape'
              },
              {
                title: '3D Baskı vs İşleme',
                excerpt: 'Prototipleme için doğru yöntem seçimi rehberi.',
                date: '12 Aralık 2024',
                slug: '3d-baski-mi-isleme-mi-prototipleme-icin-dogru-secim',
                image: 'https://readdy.ai/api/search-image?query=3D%20printer%20creating%20prototype%20next%20to%20CNC%20machined%20part%20comparison%2C%20modern%20prototyping%20facility%20with%20red%20and%20white%20design%20elements&width=400&height=250&seq=home-blog2&orientation=landscape'
              },
              {
                title: 'Kaynak Kalite Kontrol',
                excerpt: 'Kaynak sonrası muayene yöntemleri ve standartları.',
                date: '10 Aralık 2024',
                slug: 'kaynak-sonrasi-muayene-yontemleri-ve-kalite-kontrol',
                image: 'https://readdy.ai/api/search-image?query=Welding%20quality%20inspection%20with%20NDT%20testing%20equipment%2C%20professional%20welding%20workshop%2C%20red%20and%20white%20industrial%20lighting%2C%20quality%20control%20process&width=400&height=250&seq=home-blog3&orientation=landscape'
              }
            ].map((post, index) => (
              <Card key={index} hover className="overflow-hidden">
                <img 
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-2">{post.date}</div>
                  <h3 className="text-xl font-semibold text-[#161616] mb-3">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {post.excerpt}
                  </p>
                  <Link to={`/blog/${post.slug}`}>
                    <Button size="sm" variant="outline" className="whitespace-nowrap">
                      Devamını Oku
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/blog">
              <Button size="lg" variant="outline" className="whitespace-nowrap">
                Tüm Blog Yazıları
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Projenizi Hayata Geçirin
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Çiziminizi yükleyin, aynı gün teklif alın. Hızlı, güvenilir ve kaliteli üretim için bizimle iletişime geçin.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/teklif-al">
              <Button size="lg" className="whitespace-nowrap">
                <i className="ri-file-text-line mr-2"></i>
                Teklif Al
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#161616] mb-2">Adres</h3>
              <p className="text-gray-600">
                1459. Cad, İvedik OSB<br />
                Özanadolu Sanayi Sitesi No:3<br />
                06480 Yenimahalle/Ankara
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#161616] mb-2">Telefon</h3>
              <p className="text-gray-600">0541 456 56 35</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#161616] mb-2">E-posta</h3>
              <p className="text-gray-600">info@3dpmakina.com</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
